import React from 'react';
import { AlertTriangle, Info, AlertCircle, ArrowRightLeft } from 'lucide-react';
import type { FinancialRiskFinding } from '../../services/api';

interface FinancialRiskFlagCardProps {
  finding: FinancialRiskFinding;
  onCompare?: (finding: FinancialRiskFinding) => void;
}

const severityConfig: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  critical: {
    label: 'Critical',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: <AlertCircle className="h-4 w-4" />,
  },
  warning: {
    label: 'Warning',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  info: {
    label: 'Info',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: <Info className="h-4 w-4" />,
  },
};

const flagTypeLabels: Record<string, string> = {
  urgency_language: 'Urgency Language',
  payment_timeline: 'Payment Timeline',
  penalty_clause: 'Penalty Clause',
  mlr_requirement: 'MLR Requirement',
  cost_sharing: 'Cost Sharing',
};

function highlightMatch(text: string, start: number, end: number): React.ReactNode {
  if (start < 0 || end > text.length || start >= end) {
    return text;
  }
  return (
    <>
      {text.slice(0, start)}
      <mark className="bg-yellow-200 px-0.5 rounded" data-testid="highlight-match">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

export const FinancialRiskFlagCard: React.FC<FinancialRiskFlagCardProps> = ({
  finding,
  onCompare,
}) => {
  const severity = severityConfig[finding.severity] || severityConfig.info;
  const flagLabel = flagTypeLabels[finding.flagType] || finding.flagType;

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
      data-testid={`flag-card-${finding.id}`}
    >
      {/* Header: severity badge + flag type chip */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${severity.bgColor} ${severity.color}`}
          data-testid="severity-badge"
        >
          {severity.icon}
          {severity.label}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {flagLabel}
        </span>
        <span className="text-xs text-gray-400 ml-auto">
          {finding.ruleName}
        </span>
      </div>

      {/* Clause text with highlighted match */}
      <div className="text-sm text-gray-700 mb-3 leading-relaxed bg-gray-50 p-3 rounded" data-testid="clause-text">
        {highlightMatch(finding.clauseText, finding.charOffsetStart, finding.charOffsetEnd)}
      </div>

      {/* Rationale */}
      <p className="text-sm text-gray-600 italic mb-3" data-testid="rationale">
        {finding.rationale}
      </p>

      {/* Compare button */}
      {onCompare && (
        <button
          onClick={() => onCompare(finding)}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
          data-testid="compare-button"
        >
          <ArrowRightLeft className="h-4 w-4 mr-1" />
          Compare with Another Contract
        </button>
      )}
    </div>
  );
};
